import { NgIf, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  PredictiveSearch,
  RecentListItem
} from '@com-delta-idp/mnx-sitewide-shared-core-ui';
import { GenericAction, SelectedData, Tab } from '@com-delta-omnichannel/mach';
import { TripTypeOptions } from '../../constants/book-widget.constants';
import { BookWidgetAem } from '../../models/book-widget-aem.interfaces';
import { BookWidgetFieldUpdateEvent } from '../../models/book-widget-fields-update-info.interfaces';
import { BookWidgetFields } from '../../models/book-widget-fields.interfaces';
import { DatepickerConfig } from '../../models/common.interfaces';
import {
  SearchModalEvent,
  SearchModalEventType
} from '../../models/search-modal-events-object.interface';
import { GuidedNavigationErrorComponent } from '../common';
import { InfoPopUpModalComponent } from '../common/info-popup-modal/info-popup-modal.component';
import { BookWidgetDesktopSkeletonComponent } from '../skeleton-ui/apps-book-widget-desktop-skeleton/apps-book-widget-desktop-skeleton.component';
import { BookWidgetMobileSkeletonComponent } from '../skeleton-ui/apps-book-widget-mobile-skeleton/apps-book-widget-mobile-skeleton.component';
import { AppBookingWidgetExtended } from './apps-book-widget.extended';
import {
  BookWidgetOwRtDesktopComponent,
  BookWidgetOwRtMobileComponent,
  MulticityDesktopAdvanceComponent,
  MulticityMobileAdvanceComponent
} from './components';

@Component({
  standalone: true,
  selector: 'idp-apps-book-widget',
  templateUrl: './apps-book-widget.component.html',
  styleUrls: ['./apps-book-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    NgTemplateOutlet,
    BookWidgetOwRtDesktopComponent,
    BookWidgetOwRtMobileComponent,
    MulticityDesktopAdvanceComponent,
    MulticityMobileAdvanceComponent,
    FormsModule,
    InfoPopUpModalComponent,
    BookWidgetDesktopSkeletonComponent,
    BookWidgetMobileSkeletonComponent,
    GuidedNavigationErrorComponent
  ]
})
export class AppBookingWidgetComponent extends AppBookingWidgetExtended {
  override predictiveSearchData: PredictiveSearch = {
    placeholder: '',
    items: [],
    title: '',
    value: '',
    transparentSearch: true,
    errorMessage: '',
    selectLabel: ''
  };

  @Input() datepickerConfig: DatepickerConfig = {} as DatepickerConfig;
  @Input() isBookWidgetReady = false;
  @Input() isDesktop = true;
  @Input() bookWidgetFields?: BookWidgetFields;
  @Input() override bookWidgetAem?: BookWidgetAem | null;
  private _guidedNavigationErrorMsg?: string | null = null;

  @Input() set guidedNavigationErrorMsg(value: string | null | undefined) {
    this._guidedNavigationErrorMsg = value;
    if (value) {
      this.scrollToErrorMessage();
    }
  }

  get guidedNavigationErrorMsg(): string | null | undefined {
    return this._guidedNavigationErrorMsg;
  }

  @Input() searchModalTabs: Tab[] = [];
  @Input() recentSearchList: RecentListItem[] = [];
  @Input() savedSearchList: string[] = [];

  //can only be list of predictive items
  @Input() set predictiveSearchList(list: GenericAction[]) {
    this.predictiveSearchData = {
      ...this.predictiveSearchData,
      items: list || ([] as GenericAction[])
    };
  }

  @Output() override bookWidgetFieldChange =
    new EventEmitter<BookWidgetFieldUpdateEvent>();

  @Output() swapAirports = new EventEmitter<number>();
  @Output() findFlight = new EventEmitter();

  onFindFlightClick() {
    this.findFlight.emit();
    if (this.guidedNavigationErrorMsg) {
      this.scrollToErrorMessage();
    }
  }

  @Output() addFlightsForMc = new EventEmitter();
  @Output() removeFlightForMc = new EventEmitter();
  @Output() routePickerClicked = new EventEmitter<SelectedData>();
  @Output() searchModalEvent = new EventEmitter<
    SearchModalEvent<SearchModalEventType>
  >();

  @ViewChild('infoPopUpModal')
  override infoPopUpModal!: InfoPopUpModalComponent;

  tripTypeOptions = TripTypeOptions;

  constructor(elementRef: ElementRef) {
    super(elementRef);
  }

  private scrollToErrorMessage(): void {
    requestAnimationFrame(() => {
      const element = document.querySelector(
        '.apps-book-widget__guided-error'
      ) as HTMLElement;
      const focusElement = element?.querySelector(
        '.idp-error-indicator_anchor'
      ) as HTMLElement;

      if (element && focusElement) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        focusElement.focus();
      }
    });
  }
}
